import * as React from 'react'
import { emptyValidator } from '@helpers/field-validators'
import { FieldGroup, protectedInputFieldComponent, renderFieldsSimple, renderSideFields, textInputFieldComponent, } from '@shared/components/forms'
import { isConfirmPassword, isEmail, isPassword } from '@shared/helpers'
import styled from 'styled-components'
import { FlexRow, screenSizes } from '@src/styles'
import { MultiBoxField } from '@shared/components/multibox'
import { LoginSignButton } from '@shared/components/button'
import { rgba } from 'polished'

// @ts-expect-error -- react-google-recaptcha types expect React 18, we're on React 16
import ReCAPTCHA from 'react-google-recaptcha'
import { LogoHeading } from './logo-heading'
import { GoogleAuthButton } from './google-auth-button'

interface Props {
    verified: boolean
    setVerified: (verified: boolean) => void
    values?: any
    invalid?: boolean
}

declare let CAPTCHA_SITE_KEY: string

const SignUpFormWrapper = styled.div<any>`
.SignupText{
    margin: 32px 0px 24px 0px;
    font-size: 34px;
    line-height: 46px;
    font-weight: 600;
}
button[aria-disabled='true'] {
    background: ${(props: any) => rgba('#33BB40', 0.32)};
    cursor: not-allowed;
}
`

const ValidationHint = styled.div<any>`
  color: ${props => props.theme.warning};
  font-size: 13px;
  line-height: 20px;
  margin-top: -8px;
  margin-bottom: 8px;
`

const InputRow = styled<any>(FlexRow)`
  justify-content: space-between;
  align-items: center;

  div {
      flex: 1;
  }
  div + div {
    margin-left: 12px;
  }
  @media (max-width: ${screenSizes.S}px) {
    flex-direction: column;
    align-items: center;
    > div {
      width: 100%;
    }
  }
`

const SignUpLayout = styled<any>(FlexRow)`
  > div:first-child {
    height: fit-content;
  }
  @media (max-width: ${screenSizes.XL}px) {
    flex-direction: column;
  }
`

const StyledRow = styled.div<any>`
  color: ${props => props.theme.white};
  font-size: 14px;
`

function nameFields(): FieldGroup<any> {
    return [
        {
            name: 'first',
            label: 'First Name',
            required: true,
            asterisks: false,
            validate: emptyValidator,
            placeholder: 'John',
            component: textInputFieldComponent
        },
        {
            name: 'last',
            label: 'Last Name',
            required: true,
            asterisks: false,
            validate: emptyValidator,
            placeholder: 'Doe',
            component: textInputFieldComponent
        },
    ]
}

function signUpFields(values: any): FieldGroup<any> {
    return [
        {
            name: 'email',
            label: 'Email Address',
            required: true,
            asterisks: false,
            placeholder: 'example@gmail.com',
            validate: isEmail,
            component: textInputFieldComponent,
        },
        {
            name: 'password',
            label: 'Password',
            required: true,
            asterisks: false,
            placeholder: 'Minimum 8 characters',
            validate: isPassword,
            component: protectedInputFieldComponent({}, 'text', true),
            note: "Minimum 8 characters"
        },
        {
            name: 'passwordConfirm',
            label: 'Confirm Password',
            required: true,
            asterisks: false,
            placeholder: 'Re-enter password',
            validate: isConfirmPassword(values),
            component: protectedInputFieldComponent({}, 'text', true),
            note: "Minimum 8 characters"
        },
    ]
}

function getMissingRequirements(values: any, invalid: boolean | undefined, verified: boolean): string {
    const missing: string[] = []
    if (!values?.first || !values?.last) missing.push('name')
    if (!values?.email) missing.push('email')
    if (!values?.password) missing.push('password')
    if (invalid) missing.push('valid fields')
    if (!values?.acceptedTerms) missing.push('privacy policy consent')
    if (!!CAPTCHA_SITE_KEY && !verified) missing.push('CAPTCHA verification')
    if (missing.length === 0) return ''
    return `Please complete: ${missing.join(', ')}`
}

export const SignUpForm = (props: Props) => {
    const { setVerified, values = {}, verified, invalid } = props
    const [submitted, setSubmitted] = React.useState(false)

    React.useEffect(() => {
        if (!CAPTCHA_SITE_KEY) setVerified(true)
    }, [setVerified])

    return (
        <SignUpFormWrapper>
            <LogoHeading />
            <div className='SignupText'>Sign Up</div>
            <GoogleAuthButton
                buttonText="Sign up with Google"
                mode="signup"
            />
            <InputRow>
                {renderSideFields(nameFields(), true)}
            </InputRow>
            {renderFieldsSimple(signUpFields(values), true)}
            <FlexRow>
                <MultiBoxField
                    name="acceptedTerms"
                    id="acceptedTerms"
                    text={
                        <StyledRow>
                            I agree and consent to the{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer">
                                Privacy Policy{' '}
                            </a>
                            and{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer">
                                Terms of Use{' '}
                            </a>
                        </StyledRow>
                    }
                    type="checkbox"
                />
            </FlexRow>
            {CAPTCHA_SITE_KEY && (
                <ReCAPTCHA
                    sitekey={CAPTCHA_SITE_KEY}
                    onChange={(token: string | null) => setVerified(token !== null)}
                    onExpired={() => setVerified(false)}
                    onErrored={() => setVerified(false)}
                />
            )}
            <LoginSignButton
                type="submit"
                disabled={!values?.acceptedTerms || !values?.email || !values?.password || invalid || (!!CAPTCHA_SITE_KEY && !verified)}
                onClick={() => setSubmitted(true)}
            >
                SIGN UP
            </LoginSignButton>
            {submitted && (!values?.acceptedTerms || !values?.email || !values?.password || invalid || (!!CAPTCHA_SITE_KEY && !verified)) && (
                <ValidationHint>
                    {getMissingRequirements(values, invalid, verified)}
                </ValidationHint>
            )}
        </SignUpFormWrapper>
    )
}
